import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getSuperAdminHeader } from './SuperAdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Paintbrush, Pencil, Loader2, Eye } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SuperAdminWhiteLabelPage() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editTenant, setEditTenant] = useState(null);
  const [form, setForm] = useState({ app_name: '', primary_color: '#000000', accent_color: '#EF4444', logo_url: '', favicon_url: '', custom_css: '' });
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/super/whitelabel`, { headers: getSuperAdminHeader() });
      setTenants(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const openEdit = async (t) => {
    try {
      const res = await axios.get(`${API}/super/whitelabel/${t.tenant_id}`, { headers: getSuperAdminHeader() });
      setForm({
        app_name: res.data.app_name || 'OneView',
        primary_color: res.data.primary_color || '#000000',
        accent_color: res.data.accent_color || '#EF4444',
        logo_url: res.data.logo_url || '',
        favicon_url: res.data.favicon_url || '',
        custom_css: res.data.custom_css || '',
      });
      setEditTenant(t);
    } catch (err) { toast.error('Erro ao carregar'); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/super/whitelabel/${editTenant.tenant_id}`, form, { headers: getSuperAdminHeader() });
      toast.success('White-label atualizado');
      setEditTenant(null);
      fetchData();
    } catch (err) { toast.error('Erro ao atualizar'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>;

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold">White-Label por Tenant</h2>

      <Card className="bg-white border-gray-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Empresa</TableHead>
                <TableHead className="w-36">Nome do App</TableHead>
                <TableHead className="w-28">Cor Principal</TableHead>
                <TableHead className="w-28">Cor Destaque</TableHead>
                <TableHead className="w-24">Status</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {tenants.map(t => (
                <TableRow key={t.tenant_id}>
                  <TableCell className="font-medium">{t.company_name}</TableCell>
                  <TableCell className="text-sm">{t.app_name}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded border" style={{ backgroundColor: t.primary_color }} />
                      <span className="text-xs font-mono text-gray-500">{t.primary_color}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded border" style={{ backgroundColor: t.accent_color }} />
                      <span className="text-xs font-mono text-gray-500">{t.accent_color}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {t.configured ? <Badge className="bg-green-500 text-white text-xs">Customizado</Badge> : <Badge variant="secondary" className="text-xs">Padrão</Badge>}
                  </TableCell>
                  <TableCell><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(t)}><Pencil className="h-3.5 w-3.5" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!editTenant} onOpenChange={(v) => { if (!v) setEditTenant(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Paintbrush className="h-5 w-5" />{editTenant?.company_name} — White-Label</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome do Aplicativo</Label>
              <Input value={form.app_name} onChange={(e) => setForm({ ...form, app_name: e.target.value })} placeholder="OneView" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Cor Principal (Sidebar)</Label>
                <div className="flex gap-2">
                  <input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="w-10 h-10 rounded cursor-pointer border" />
                  <Input value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="font-mono" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Cor Destaque (Botões)</Label>
                <div className="flex gap-2">
                  <input type="color" value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="w-10 h-10 rounded cursor-pointer border" />
                  <Input value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="font-mono" />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label>URL do Logo</Label>
              <Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://..." />
            </div>
            <div className="space-y-2">
              <Label>URL do Favicon</Label>
              <Input value={form.favicon_url} onChange={(e) => setForm({ ...form, favicon_url: e.target.value })} placeholder="https://..." />
            </div>
            <div className="space-y-2">
              <Label>CSS Customizado</Label>
              <Textarea rows={3} value={form.custom_css} onChange={(e) => setForm({ ...form, custom_css: e.target.value })} placeholder=".sidebar { background: #1a1a2e; }" className="font-mono text-xs" />
            </div>

            {/* Preview */}
            <div className="border rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-2">Preview</p>
              <div className="flex items-center gap-3 p-3 rounded" style={{ backgroundColor: form.primary_color }}>
                {form.logo_url ? <img src={form.logo_url} alt="logo" className="h-6" /> : null}
                <span className="font-bold text-white">{form.app_name || 'OneView'}<span style={{ color: form.accent_color }}>.</span></span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTenant(null)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}>{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
